import { Navigate, useLocation } from 'react-router';
import { useAuthStore } from '@/stores/auth';
import { AppLoader } from './AppLoader';

interface AuthGateProps {
  children: React.ReactNode;
  /** When true, render children only if authenticated; redirect otherwise. */
  requireAuth?: boolean;
  /** When true, only render if NOT authenticated (e.g. /welcome). */
  requireGuest?: boolean;
}

/**
 * Auth boundary. Reads `useAuthStore.status`:
 *   - 'unknown'         → AppLoader (cold-start refresh in flight)
 *   - 'authenticated'   → render or redirect to '/' if requireGuest
 *   - 'unauthenticated' → render or redirect to '/welcome' if requireAuth
 */
export function AuthGate({ children, requireAuth, requireGuest }: AuthGateProps) {
  const status = useAuthStore((s) => s.status);
  const location = useLocation();

  if (status === 'unknown') return <AppLoader />;

  if (requireAuth && status !== 'authenticated') {
    return <Navigate to="/welcome" replace state={{ from: location.pathname }} />;
  }

  if (requireGuest && status === 'authenticated') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
